import { supabase } from './supabase'

/**
 * The public-launch countdown. SHL-16.
 *
 * ── THE NUMBER COMES FROM THE SERVER, AND THAT IS THE WHOLE POINT ───────────
 *
 * `CountdownCard` has said "counted against each site's own timezone, not the
 * browser's" since the day it was written, while the number underneath it was
 * a constant in `lib/placeholder.ts`. That constant said 38 days to 7 October
 * on a day when it was 17 — it had been wrong since 30 August and nothing
 * could have told anybody, because a hardcoded number cannot go stale
 * visibly. The card was describing a property its own data did not have, which
 * is standing rule 4's worst shape.
 *
 * `launch_countdown()` computes the days in the database against
 * `app.resolve_person_timezone` — rule 9, and rule 11's one home for that
 * question. A crew in Santiago and a crew in Burnaby get their own answer, and
 * neither is the machine the browser happens to be running on.
 *
 * ── THREE DATES, AND THIS IS THE THIRD ──────────────────────────────────────
 *
 * Managers 7 Oct 2026, employees 13 Oct 2026, PUBLIC 15 Apr 2027. The first
 * two are seed 008's welcome notices and belong to the people being let in.
 * This counts only to the third, which is the rebrand going public — the one
 * date that is about the company rather than about the build.
 *
 * ── THE DATE IS COMMS' DATA, NOT THE BUILD'S ────────────────────────────────
 *
 * SHL-16 says the countdown "is COMMS CONTENT... not a build-state widget".
 * Nothing here needs a deploy to change: the date and the label are a row
 * `set_launch_milestone()` writes, so moving the public launch is an UPDATE
 * (rule 17). That is why this does not live in `lib/hubNotices.tsx`, which is
 * build-state warnings only and says so.
 */
export type LaunchCountdown = {
  label: string
  publicLaunchOn: string
  daysRemaining: number
  siteTimezone: string
}

type CountdownRow = {
  label: string
  public_launch_on: string
  days_remaining: number
  site_timezone: string
}

/**
 * REFUSES rather than returning a zero when no date is set (rule 12), and the
 * caller is expected to render nothing rather than a card saying "0 days".
 * An empty list is a false claim about the world; a thrown error is a true
 * claim about the request, and Home's catch turns it into a missing card the
 * same way the star widgets handle theirs.
 */
export async function loadCountdown(): Promise<LaunchCountdown> {
  const { data, error } = await supabase.rpc('launch_countdown')
  if (error) throw error

  const row = (data as CountdownRow[] | null)?.[0]
  if (!row) throw new Error('no public launch date is set')

  return {
    label: row.label,
    publicLaunchOn: row.public_launch_on,
    daysRemaining: row.days_remaining,
    siteTimezone: row.site_timezone,
  }
}

/**
 * How the date reads under the number. Formatted from the DATE STRING's own
 * parts, never `new Date(iso)` — that parses a bare `YYYY-MM-DD` as midnight
 * UTC, so anybody west of Greenwich renders the day before. 15 April becoming
 * 14 April on every machine in Vancouver is precisely the class of bug the
 * server-side count exists to avoid, and it would be embarrassing to
 * reintroduce it one line below.
 */
export function launchDateLabel(iso: string, locale: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)))
}
